/**
 * Deltid (issue #109): each part-timer's approved hours in the last twelve finished months against
 * their agreed week (GET /admin/part-time, pay.view), with the right in arbeidsmiljøloven § 14-4 a
 * flagged when the twelve-month average passes the agreed position. Read only: it informs, it decides
 * nothing. Whether the need for the extra work is over is the employer's to document; Timefly cannot
 * know it, and the page says so.
 */
import { get } from '@/api/client';
import { T } from '@/i18n';
import { Button, LoadError, Skeleton, toast } from '@/ui';
import { PersonLabel } from '@/ui/PersonLabel';
import { fmtD, monthLabel, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Explain, Status, TabPanel } from '@/ui/page';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { csvOf } from '../dagen/today';
import { LonnHead } from './Head';

interface Month {
  month: string;
  days: number;
  agreed: number;
  worked: number;
  diff: number;
  percent: number;
}
export interface PartTimer {
  user_id: number;
  name: string;
  lastname: string;
  agreed_week_hours: number;
  agreed_percent: number;
  since: string | null;
  since_first_shift: boolean;
  months_counted: number;
  full_year: boolean;
  agreed: number;
  worked: number;
  diff: number;
  worked_percent: number;
  months_over: number;
  flag: boolean;
  months: Month[];
}
interface Report {
  from: string;
  to: string;
  full_week: number;
  people: PartTimer[];
}

const FLAG =
  'Har jevnlig arbeidet utover avtalt stilling i 12 måneder; rett til stilling tilsvarende faktisk arbeidstid (aml § 14-4 a)';
const dec = (n: number) => n.toFixed(2).replace('.', ',');

/** Why fewer than twelve months count, or nothing. */
function shortOf(p: PartTimer): string | null {
  if (p.full_year) return null;
  if (p.since_first_shift)
    return T('Ingen startdato: regnet fra første godkjente vakt, {n} av 12 måneder.', {
      n: p.months_counted,
    });
  return T('Ansatt fra {date}: {n} av 12 måneder. Loven regner 12.', {
    date: p.since ? fmtD(p.since) : '–',
    n: p.months_counted,
  });
}

export default function Deltid() {
  const q = useQuery({
    queryKey: ['part-time'],
    queryFn: () => get<Report>('/admin/part-time'),
    staleTime: 30_000,
  });
  const [open, setOpen] = useState<number | null>(null);
  const r = q.data;
  const people = r?.people ?? [];
  // nobody has a start date: said once above the table, not on every row (#272 B-41)
  const noneStart = people.length > 0 && people.every((p) => !p.full_year && p.since_first_shift);

  const exportCsv = () => {
    if (!r) return;
    const rows = [
      [
        'Ansatt',
        'Måned',
        'Dager',
        'Avtalt (t)',
        'Jobbet (t)',
        'Differanse (t)',
        'Stilling jobbet (%)',
        'Avtalt stilling (%)',
        'Flagget',
      ],
    ];
    for (const p of people) {
      for (const m of p.months) {
        rows.push([
          `${p.name} ${p.lastname}`,
          m.month,
          String(m.days),
          dec(m.agreed),
          dec(m.worked),
          dec(m.diff),
          String(m.percent).replace('.', ','),
          String(p.agreed_percent).replace('.', ','),
          p.flag ? 'Ja' : 'Nei',
        ]);
      }
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csvOf(rows)], { type: 'text/csv;charset=utf-8' }));
    a.download = `deltid-${r.from}-${r.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast(T('Deltid lastet ned'));
  };

  return (
    <div className="lonn">
      <LonnHead
        tab="deltid"
        action={
          <Button tone="pri" onClick={exportCsv} disabled={!people.length} data-testid="deltid-csv">
            <Icon name="download" />
            {T('Last ned CSV')}
          </Button>
        }
      />
      <TabPanel tab="deltid">
        <section className="mcard" data-testid="deltid">
          <p style={{ margin: 0 }}>
            {r
              ? T(
                  'Godkjente timer {from} til {to} mot avtalt arbeidstid. Full stilling er {week} timer i uka.',
                  {
                    from: fmtD(r.from),
                    to: fmtD(r.to),
                    week: num(r.full_week),
                  },
                )
              : T('Godkjente timer de siste 12 hele månedene mot avtalt arbeidstid.')}
          </p>
          <Explain q={T('Hva sier loven, og hva vet ikke Timefly?')}>
            <p>
              {T(
                'Arbeidsmiljøloven § 14-4 a: deltidsansatte som de siste 12 månedene jevnlig har arbeidet utover avtalt arbeidstid, har rett til stilling tilsvarende faktisk arbeidstid i perioden.',
              )}
            </p>
            <p>
              {T(
                'Retten gjelder ikke hvis arbeidsgiveren kan dokumentere at behovet for merarbeidet ikke lenger finnes. Det kan ikke Timefly vite. Rapporten viser timene; den avgjør ikke saken, og den endrer ingenting.',
              )}
            </p>
            <p>
              {T(
                'Loven regner de 12 månedene fra den dagen den ansatte krever stillingen. Rapporten viser de 12 siste hele månedene. Timene er de samme godkjente timene som lønnsgrunnlaget, med pause trukket. Fravær og ferie er ikke regnet inn, og avtalt arbeidstid er den som står på den ansatte i dag.',
              )}
            </p>
          </Explain>
          {q.isPending ? (
            <Skeleton />
          ) : q.isError ? (
            // one retry, and the server's words go to the log, not the page (#272 B-43)
            <LoadError what={T('rapporten')} onRetry={() => q.refetch()} />
          ) : people.length === 0 ? (
            <p style={{ color: 'var(--ink3)' }}>
              {T(
                'Ingen ansatte har avtalt arbeidstid under full stilling. Avtalt arbeidstid settes på den ansatte.',
              )}
            </p>
          ) : (
            <>
              {noneStart && (
                <p style={{ fontSize: 13, color: 'var(--ink3)' }} data-testid="deltid-nostart">
                  {T(
                    'Ingen har startdato, så månedene regnes fra første godkjente vakt. Måneder over viser hvor mange som telles. Loven regner 12.',
                  )}
                </p>
              )}
              {/* the app's own table in a card like the other report tables (#272 B-S08); a person's months
                  are rows of the same table, so they stand under the parent's columns (#272 B-S02) */}
              <div className="mcard dlt-card">
                <table className="mt cards dlt">
                  <thead>
                    <tr>
                      <th>{T('Ansatt')}</th>
                      <th>{T('Avtalt stilling')}</th>
                      <th>{T('Jobbet')}</th>
                      <th>{T('Differanse')}</th>
                      <th>{T('Måneder over')}</th>
                      <th />
                    </tr>
                  </thead>
                  {people.map((p) => (
                    <tbody
                      key={p.user_id}
                      data-testid={open === p.user_id ? `deltid-mnd-${p.user_id}` : undefined}
                    >
                      <tr data-testid={`deltid-${p.user_id}`}>
                        <td>
                          <PersonLabel id={p.user_id} name={`${p.name} ${p.lastname}`} />
                          {p.flag && (
                            <div>
                              <Status tone="warn">{T(FLAG)}</Status>
                              <div style={{ fontSize: 13, color: 'var(--ink3)' }}>
                                {T(
                                  'Timene tilsvarer {p} % stilling. Gjelder ikke hvis behovet for merarbeidet ikke lenger finnes; det vet ikke Timefly.',
                                  {
                                    p: num(p.worked_percent, 1),
                                  },
                                )}
                              </div>
                            </div>
                          )}
                          {!noneStart && shortOf(p) && (
                            <div style={{ fontSize: 13, color: 'var(--ink3)' }}>{shortOf(p)}</div>
                          )}
                        </td>
                        <td data-k={T('Avtalt stilling')}>
                          {num(p.agreed_percent, 1)} % ({T('{h} t/uke', { h: num(p.agreed_week_hours) })})
                        </td>
                        <td data-k={T('Jobbet')}>
                          {num(p.worked_percent, 1)} % ({num(p.worked)} t)
                        </td>
                        <td data-k={T('Differanse')}>{`${p.diff > 0 ? '+' : ''}${num(p.diff)} t`}</td>
                        <td data-k={T('Måneder over')}>
                          {/* no month counted yet is «–», not «0 av 0» (#272 B-42) */}
                          {p.months_counted
                            ? T('{n} av {m}', {
                                n: p.months_over,
                                m: p.months_counted,
                              })
                            : '–'}
                        </td>
                        <td className="dlt-act">
                          {/* no counted month, nothing to open: an empty list behind a button was B-S02 */}
                          {p.months.length > 0 && (
                            <Button
                              ghost
                              onClick={() => setOpen(open === p.user_id ? null : p.user_id)}
                              aria-expanded={open === p.user_id}
                            >
                              {T('Måneder')}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {open === p.user_id && (
                        <>
                          <tr className="dlt-sub">
                            <th scope="col">{T('Måned')}</th>
                            <th scope="col">{T('Avtalt')}</th>
                            <th scope="col">{T('Jobbet')}</th>
                            <th scope="col">{T('Differanse')}</th>
                            <th scope="col">{T('Stilling')}</th>
                            <th />
                          </tr>
                          {p.months.map((m) => (
                            <tr key={m.month} className="dlt-m">
                              <td>{monthLabel(m.month)}</td>
                              <td data-k={T('Avtalt')}>{num(m.agreed)} t</td>
                              <td data-k={T('Jobbet')}>{num(m.worked)} t</td>
                              <td data-k={T('Differanse')}>{`${m.diff > 0 ? '+' : ''}${num(m.diff)} t`}</td>
                              <td data-k={T('Stilling')}>{num(m.percent, 1)} %</td>
                              <td />
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                  ))}
                </table>
              </div>
            </>
          )}
        </section>
      </TabPanel>
    </div>
  );
}
